import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getClients, type ClientRosterRow } from '../lib/advisorApi';
import { formatDate } from '../lib/format';
import './advisor.css';

type Sort = 'alpha' | 'checkin_due' | 'status';

export function ClientsScreen() {
  const { api } = useAuth();
  const [rows, setRows] = useState<ClientRosterRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('alpha');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const res = await getClients(api, { search: search.trim(), sort });
          if (!cancelled) setRows(res.rows);
        } catch {
          if (!cancelled) setError('We could not load your clients just now.');
        }
      })();
    }, 200); // debounce the search field
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, search, sort]);

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">Your book</p>
        <h1 className="page__title">Clients</h1>
        <p className="page__sub">Every client you have invited, and the state of their vault.</p>
      </header>

      <div className="toolbar">
        <input
          className="field"
          type="search"
          placeholder="Search by name"
          aria-label="Search clients by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="field"
          aria-label="Sort clients"
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
        >
          <option value="alpha">Name</option>
          <option value="checkin_due">Check-in due</option>
          <option value="status">Vault status</option>
        </select>
      </div>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="la-card page__empty">
          <p>No clients yet.</p>
          <div className="la-cta-wrap">
            <Link to="/invites" className="la-btn-primary">
              Invite your first client
            </Link>
          </div>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="la-card tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Vault</th>
                <th>Messages</th>
                <th>Sealed</th>
                <th>Recipients</th>
                <th>Check-in due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.clientUserId}>
                  <td className="table__name">
                    {r.fullName}
                    <div className="pill" style={{ marginTop: 6 }}>
                      {r.email}
                    </div>
                  </td>
                  <td>
                    {r.vaultId === null ? (
                      <span className="pill">Not started</span>
                    ) : (
                      <Link className="table__link" to={`/clients/${r.vaultId}`}>
                        {r.vaultStatus ?? 'open'}
                      </Link>
                    )}
                  </td>
                  <td>{r.messageCount}</td>
                  <td>{r.sealedCount}</td>
                  <td>{r.recipientCount}</td>
                  <td>{formatDate(r.nextCheckinDueAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
